import _ from 'lodash'
import { firestore } from '@/services/firebase'

export default {
    data() {
        return {
            projectPackingListsListener: null,
            currentPackingListListener: null,
            highlightedPackageIds: [],
            realtimeCurrentUserId: null,
            hasReceivedInitialProjectPackingListsSnapshot: false,
            hasReceivedInitialCurrentPackingListSnapshot: false,
            completedProjectPackingLists: [],
        }
    },
    beforeDestroy() {
        this.teardownPackingListRealtimeListeners()
    },
    methods: {
        teardownPackingListRealtimeListeners() {
            if (this.projectPackingListsListener) {
                this.projectPackingListsListener()
                this.projectPackingListsListener = null
            }
            if (this.currentPackingListListener) {
                this.currentPackingListListener()
                this.currentPackingListListener = null
            }
            this.hasReceivedInitialProjectPackingListsSnapshot = false
            this.hasReceivedInitialCurrentPackingListSnapshot = false
            this.completedProjectPackingLists = []
        },
        getCurrentPackingListDocId() {
            return this.originalOrder?.id || this.packingList?.id
        },
        setupProjectPackingListsListener() {
            if (!this.project?.id || !this.companyId) {
                return
            }
            try {
                const query = firestore
                    .collection('companies')
                    .doc(this.companyId)
                    .collection('packingLists')
                    .where('projectId', '==', this.project.id)

                this.projectPackingListsListener = query.onSnapshot(
                    snapshot => {
                        const isInitialSnapshot = !this
                            .hasReceivedInitialProjectPackingListsSnapshot
                        this.hasReceivedInitialProjectPackingListsSnapshot = true

                        if (
                            isInitialSnapshot ||
                            snapshot.metadata.fromCache ||
                            snapshot.metadata.hasPendingWrites
                        ) {
                            return
                        }

                        const updatedPackingLists = []
                        snapshot.forEach(doc => {
                            updatedPackingLists.push({
                                id: doc.id,
                                ...doc.data(),
                            })
                        })

                        const liveIds = new Set(
                            updatedPackingLists.map(pl => pl.id)
                        )
                        const knownCompletedIds = new Set(
                            this.completedProjectPackingLists.map(pl => pl.id)
                        )
                            ; (this.projectPackingLists || []).forEach(pl => {
                                if (
                                    pl.status === 'completed' &&
                                    !liveIds.has(pl.id) &&
                                    !knownCompletedIds.has(pl.id)
                                ) {
                                    this.completedProjectPackingLists.push(pl)
                                    knownCompletedIds.add(pl.id)
                                }
                            })

                        this.projectPackingLists = [
                            ...updatedPackingLists,
                            ...this.completedProjectPackingLists,
                        ]
                        this.formatPackingItems()
                        this.formatWorkOrders()
                        this.formatBOMs()
                    },
                    error => {
                        console.error(
                            'Error listening to project packing lists:',
                            error
                        )
                        this.setErrorItems({
                            source: this.$options.name,
                            message: 'Error listening to packing lists updates',
                        })
                    }
                )
            } catch (error) {
                console.error(
                    'Error setting up project packing lists listener:',
                    error
                )
            }
        },
        setupCurrentPackingListListener() {
            const packingListId = this.getCurrentPackingListDocId()
            if (!packingListId || !this.companyId) {
                return
            }
            try {
                const docRef = firestore
                    .collection('companies')
                    .doc(this.companyId)
                    .collection('packingLists')
                    .doc(packingListId)

                this.currentPackingListListener = docRef.onSnapshot(
                    snapshot => {
                        if (!snapshot.exists) {
                            return
                        }

                        const isInitialSnapshot = !this
                            .hasReceivedInitialCurrentPackingListSnapshot
                        this.hasReceivedInitialCurrentPackingListSnapshot = true

                        if (isInitialSnapshot) {
                            return
                        }

                        const updatedPackingList = {
                            id: snapshot.id,
                            ...snapshot.data(),
                        }

                        if (
                            this.realtimeCurrentUserId &&
                            updatedPackingList.updatedBy ===
                            this.realtimeCurrentUserId
                        ) {
                            return
                        }

                        if (this.packageForm) {
                            this.mergeRemotePackages(updatedPackingList)
                            return
                        }

                        this.applyCurrentPackingListSnapshot(updatedPackingList)
                    },
                    error => {
                        console.error(
                            'Error setting up current packing list listener:',
                            error
                        )
                    }
                )
            } catch (error) {
                console.error(
                    'Error setting up current packing list listener:',
                    error
                )
            }
        },
        mergeRemotePackages(updatedPackingList) {
            const oldPackages = this.packingList?.packages || []
            const newPackages = updatedPackingList.packages || []

            this.highlightNewPackages(oldPackages, newPackages)

            this.packingList = {
                ...this.packingList,
                packages: _.cloneDeep(newPackages),
                totalMass: updatedPackingList.totalMass,
                selectedWorkOrders: updatedPackingList.selectedWorkOrders,
                selectedBOMs: updatedPackingList.selectedBOMs,
                reservedWorkOrders: updatedPackingList.reservedWorkOrders,
                updatedBy: updatedPackingList.updatedBy,
                updatedOn: updatedPackingList.updatedOn,
            }

            if (typeof this.getSelectedWorkOrders === 'function') {
                this.getSelectedWorkOrders()
            }
            if (typeof this.getSelectedBOMs === 'function') {
                this.getSelectedBOMs()
            }
            if (typeof this.getSelectedWorkOrdersAndBOMs === 'function') {
                this.getSelectedWorkOrdersAndBOMs()
            }

            this.formatPackingItems()
            this.formatWorkOrders()
            this.formatBOMs()

            if (
                this.selectedPackage &&
                this.selectedPackage.id &&
                this.packingList.packages
            ) {
                const updatedPackage = this.packingList.packages.find(
                    p => p.id === this.selectedPackage.id
                )
                if (
                    updatedPackage &&
                    this.hasPackageChanged(this.selectedPackage, updatedPackage)
                ) {
                    this.selectedPackage = _.cloneDeep(updatedPackage)
                }
            }

            this.$emit('replacePackingList', this.packingList)
        },
        applyCurrentPackingListSnapshot(updatedPackingList) {
            const oldPackages = this.packingList?.packages || []

            this.packingList = _.cloneDeep(updatedPackingList)
            this.highlightNewPackages(
                oldPackages,
                updatedPackingList.packages || []
            )

            if (
                this.packingList.deliveryInformation &&
                this.deliveryInfoString !== undefined
            ) {
                this.deliveryInfoString =
                    this.packingList.deliveryInformation.location +
                    ' - ' +
                    this.packingList.deliveryInformation.contact +
                    ' - ' +
                    this.packingList.deliveryInformation.phone
            }

            if (
                this.packingList.createdBy &&
                typeof this.findUserName === 'function'
            ) {
                this.createdBy = this.findUserName(this.packingList.createdBy)
            }

            if (typeof this.getSelectedWorkOrders === 'function') {
                this.getSelectedWorkOrders()
            }
            if (typeof this.getSelectedBOMs === 'function') {
                this.getSelectedBOMs()
            }
            if (typeof this.getSelectedWorkOrdersAndBOMs === 'function') {
                this.getSelectedWorkOrdersAndBOMs()
            }

            this.formatPackingItems()
            this.formatWorkOrders()
            this.formatBOMs()

            if (
                this.selectedPackage &&
                this.selectedPackage.id &&
                this.packingList.packages
            ) {
                const updatedPackage = this.packingList.packages.find(
                    p => p.id === this.selectedPackage.id
                )
                if (
                    updatedPackage &&
                    this.hasPackageChanged(this.selectedPackage, updatedPackage)
                ) {
                    this.selectedPackage = _.cloneDeep(updatedPackage)
                }
            }

            this.$emit('replacePackingList', this.packingList)
        },
        hasPackageChanged(oldPackage, newPackage) {
            const oldItemsStr = JSON.stringify(oldPackage.items || [])
            const newItemsStr = JSON.stringify(newPackage.items || [])
            const itemsChanged = oldItemsStr !== newItemsStr
            const dimensionsChanged = !_.isEqual(
                oldPackage.packageDimensions,
                newPackage.packageDimensions
            )
            const bomChanged = oldPackage.packageBOM !== newPackage.packageBOM
            return itemsChanged || dimensionsChanged || bomChanged
        },
        highlightNewPackages(oldPackages, newPackages) {
            const oldIds = new Set((oldPackages || []).map(pkg => pkg.id))
            const addedIds = (newPackages || [])
                .filter(pkg => pkg.id && !oldIds.has(pkg.id))
                .map(pkg => pkg.id)

            if (addedIds.length === 0) {
                return
            }

            this.highlightedPackageIds = [
                ...new Set([
                    ...(this.highlightedPackageIds || []),
                    ...addedIds,
                ]),
            ]

            setTimeout(() => {
                this.highlightedPackageIds = (
                    this.highlightedPackageIds || []
                ).filter(id => !addedIds.includes(id))
            }, 4000)
        },
        isPackageHighlighted(packageId) {
            return (this.highlightedPackageIds || []).includes(packageId)
        },
    },
}
